// src/api/content.ts
//
// Content written from the brand record for an action (#132). Backend:
// app/services/content_studio/ (brief.py, drafting.py, claims.py,
// service.py) and app/api/content_routes.py.
//
// A brief is assembled for one action from the brand record and stored; every
// draft is written from a brief. Each draft's versions carry a claim check:
// every sentence stating a number, date, name, place, price, certification or
// comparison is supported (with the record entry), unverifiable, or
// contradicted. Unverifiable and contradicted claims block the send to
// publish until a person edits, confirms as a fact, or removes them.
// Counts are only in an admin's payload.

import { apiFetch } from "./api-fetch";

export type AssetType = "page" | "faq" | "comparison" | "customer_story" | "facts_entry" | "post";
export type BriefMode = "action" | "facts_only" | "free_text";
export type ClaimStatus = "supported" | "unverifiable" | "contradicted";
export type Resolution = "edit" | "confirm_fact" | "remove";

export const ASSET_TYPES: { id: AssetType; label: string }[] = [
  { id: "page", label: "Page" },
  { id: "faq", label: "FAQ block" },
  { id: "comparison", label: "Comparison" },
  { id: "customer_story", label: "Customer story" },
  { id: "facts_entry", label: "Facts-layer entry" },
  { id: "post", label: "Post" },
];

export interface RecordRef {
  id: number | null;
  type: string | null;
  key: string | null;
  source_kind: string | null;
  source_ref: string | null;
  source_label: string | null;
  observed_at: string | null;
  confidence: string | null;
}

export interface BriefEntry {
  ref: string;
  kind: "belief" | "quote" | "win_reason" | "fact" | "market" | "precedent" | "action";
  kind_label: string;
  statable: boolean;
  label: string;
  text: string;
  record: RecordRef | null;
  persona?: string | null;
  competitor?: string;
}

export interface Brief {
  version: string;
  mode: BriefMode;
  mode_note: string;
  evidence: boolean;
  brand: { name: string; display_name: string; website: string };
  action: { id: number; title: string; generator_label?: string; statement: string; read_label?: string } | null;
  belief: {
    id: string; name: string; summary: string; claim: string;
    missing_personas: string[]; missing_question_types: string[];
    presence: { share: number | null }; primary_when_present: { share: number | null };
  } | null;
  free_text: string;
  entries: BriefEntry[];
  voice: Record<string, any> | null;
  voice_missing: boolean;
  rules: string[];
  run_id: number | null;
  truth_set_version_id: number | null;
  record_entry_ids: number[];
}

export interface BriefView {
  id: number;
  customer_id: number;
  action_id: number | null;
  run_id: number | null;
  truth_set_version_id: number | null;
  mode: BriefMode;
  mode_note: string;
  evidence: boolean;
  record_entry_ids: number[];
  blueprint_item_id: string | null;
  created_at: string;
  brief: Brief;
}

export interface Claim {
  sentence_index: number;
  sentence: string;
  kinds: string[];
  status: ClaimStatus;
  entries: string[];
  support: { ref: string; kind: string; label: string; text: string; record: RecordRef | null }[];
  note: string;
  source: "rule" | "model";
  /** A model pass read this sentence. A supported claim without it (written
   *  after the last check, or the check did not run) still blocks publish. */
  model_checked?: boolean;
  resolution?: { action: Resolution; by: string; at?: string; note?: string; previous_status?: ClaimStatus };
}

export interface ClaimSummary {
  supported: number;
  unverifiable: number;
  contradicted: number;
  total: number;
  /** Unverifiable or contradicted, not yet resolved. */
  unresolved?: number;
  /** Supported, but no model pass has read the sentence yet. */
  unchecked?: number;
  blocking: number;
  publish_ready: boolean;
  model_check?: string;
}

export interface Signal {
  ref: string;
  kind: string;
  kind_label: string;
  label: string;
  text: string;
  record: RecordRef | null;
}

export interface DraftVersion {
  id: number;
  version_number: number;
  author_kind: "model" | "person";
  author: string;
  brief_id: number;
  change_kind: "drafted" | "edited" | "claim_confirmed" | "claim_removed" | "rechecked";
  change_note: string;
  title: string;
  body: string;
  claims: Claim[];
  claim_summary: ClaimSummary;
  signals: Signal[];
  policy_warnings: { field: string; severity: "hard" | "soft"; message: string }[];
  belief_explicit: boolean | null;
  created_at: string;
  publish_blockers: string[];
  model?: string | null;
  cost_usd?: number | null;
}

export interface Draft {
  id: number;
  customer_id: number;
  asset_type: AssetType;
  asset_label: string;
  created_at: string;
  sent_at: string | null;
  action_item_id: number | null;
  brief: BriefView;
  latest: DraftVersion;
  versions: { version_number: number; author_kind: string; author: string; change_kind: string;
              change_note: string; created_at: string; claim_summary: Partial<ClaimSummary> }[];
}

export interface DraftSummary {
  id: number;
  brief_id: number;
  action_id: number | null;
  asset_type: AssetType;
  asset_label: string;
  mode: BriefMode | null;
  belief_id: string | null;
  belief_name: string | null;
  action_title: string | null;
  blueprint_item_id: string | null;
  title: string;
  version_number: number;
  claim_summary: Partial<ClaimSummary>;
  sent_at: string | null;
  created_at: string;
}

export interface ContentList {
  available: boolean;
  migration?: string;
  briefs: { id: number; action_id: number | null; mode: BriefMode; action_title: string | null;
            belief_id: string | null; belief_name: string | null; free_text: string; created_at: string }[];
  drafts: DraftSummary[];
}

const base = (customerId: number) => `/content/customers/${customerId}`;

export async function listContent(customerId: number, actionId?: number): Promise<ContentList> {
  return apiFetch(`${base(customerId)}${actionId != null ? `?action_id=${actionId}` : ""}`);
}

export async function createBrief(
  customerId: number,
  body: { action_id?: number | null; free_text?: string; blueprint_item_id?: string | null },
): Promise<BriefView> {
  return apiFetch(`${base(customerId)}/briefs`, { method: "POST", body: JSON.stringify(body) });
}

/** "Write from this action": the brief, then one draft per asset type. */
export async function writeFromAction(
  customerId: number,
  actionId: number,
  body: { asset_types: AssetType[]; instructions?: string; blueprint_item_id?: string | null },
): Promise<{ brief: BriefView; drafts: Draft[] }> {
  return apiFetch(`${base(customerId)}/actions/${actionId}/write`, { method: "POST", body: JSON.stringify(body) });
}

export async function getDraft(customerId: number, draftId: number): Promise<Draft> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}`);
}

export async function getDraftVersion(customerId: number, draftId: number, n: number): Promise<DraftVersion> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/versions/${n}`);
}

export async function editDraft(
  customerId: number, draftId: number,
  body: { base_version: number; body: string; title?: string; note?: string },
): Promise<Draft> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/edit`, { method: "POST", body: JSON.stringify(body) });
}

export async function resolveClaim(
  customerId: number, draftId: number, sentenceIndex: number,
  body: { base_version: number; resolution: Resolution; note?: string; body?: string; title?: string },
): Promise<Draft> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/claims/${sentenceIndex}/resolve`, {
    method: "POST", body: JSON.stringify(body),
  });
}

export async function recheckDraft(customerId: number, draftId: number, baseVersion: number): Promise<Draft> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/recheck`, {
    method: "POST", body: JSON.stringify({ base_version: baseVersion }),
  });
}

/** Admin: the latest version becomes a content item on its action. Refused
 *  while any claim is unverifiable or contradicted. Nothing goes live here. */
export async function sendDraft(customerId: number, draftId: number, versionNumber: number): Promise<Draft> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/send`, {
    method: "POST", body: JSON.stringify({ version_number: versionNumber }),
  });
}

export interface PublisherContext {
  customer_id: number | null;
  available: boolean;
  actions: { id: number; title: string; status: string; generator_label: string; belief: string | null }[];
  note?: string;
}

export async function getPublisherContext(brandName: string): Promise<PublisherContext> {
  return apiFetch(`/content/publisher-context?brand_name=${encodeURIComponent(brandName)}`);
}

// ── From draft to publication (#133) ─────────────────────────────────────────
// Backend: app/services/content_studio/gate.py (the rules) and
// publish_service.py. A version goes live only with a wind tunnel result on
// that exact text, a clean claim check and an approval by someone other than
// the person who asked (or, on a team of one, the same person confirming
// twice; or an admin override with a reason).

export type Verdict = "PASS" | "CONDITIONAL" | "FAIL";
export type ApprovalKind = "approval" | "self_confirmed" | "admin_override";

export interface TestBelief {
  id: string; label: string; scale: string; targeted: boolean;
  control: number | null; value: number | null; delta: number | null; n?: number;
  ci95?: [number, number] | null; status: string | null; shade: string | null;
}

export interface DraftTest {
  id: number; draft_id: number; version_number: number; wind_tunnel_run_id: number; wind_tunnel_slot: string;
  scope: "belief" | "battery"; belief_ids: string[]; question_ids: string[]; question_types: string[];
  status: "pending" | "completed" | "cancelled" | "failed"; complete: boolean;
  verdict: Verdict | null; verdict_rule: string | null;
  result_json: {
    beliefs?: TestBelief[]; visibility?: TestBelief[]; read?: string | null;
    retrieval?: Record<string, any>[]; cost?: { total_usd?: number; spend_cap_usd?: number }; stop_reason?: string | null;
  } | null;
  requested_by: string; requested_at: string; settled_at: string | null;
}

export interface Approval {
  id: number; draft_id: number; version_number: number; requested_by: string; requested_at: string;
  request_note: string; status: "requested" | "approved" | "withdrawn"; kind: ApprovalKind | null;
  kind_label: string | null; approved_by: string | null; approved_at: string | null; reason: string;
  withdrawn_by: string | null; withdrawn_at: string | null;
}

export interface GateBlocker { code: string; message: string }

export interface Publication {
  id: number; customer_id: number; origin: "brand_publisher" | "assistant" | "hand_entry";
  run_id: number | null; action_id: number | null; action_item_id: number | null;
  draft_id: number | null; draft_version: number | null; belief_ids: string[]; question_types: string[];
  question_ids: string[]; title: string; content_hash: string; destination: string; url: string | null;
  published_at: string; approved_by: string; approval_kind: string | null; approval_kind_label: string;
  wind_tunnel_run_id: number | null; wind_tunnel_verdict: Verdict | null; note: string;
  recheck_status: "not_applicable" | "scheduled" | "running" | "completed" | "failed" | "skipped";
  recheck_due_at: string | null; recheck_run_id: number | null; recheck_attempts: number;
  recheck_result: { lanes?: Record<string, any>[]; status?: string; complete?: boolean; cost?: Record<string, number> } | null;
  recheck_note: string;
}

export interface PublishState {
  available: boolean;
  migration?: string;
  gate?: { version_number: number; ready: boolean; blockers: GateBlocker[]; test: DraftTest | null; approval: Approval | null };
  tests?: DraftTest[];
  approvals?: Approval[];
  can_self_approve?: boolean;
  open_request_ids?: number[];
  publications?: Publication[];
  log?: { id: number; kind: string; actor: string; at: string; version_number: number | null; detail: Record<string, any> }[];
}

export interface TestPlan {
  scope: "belief" | "battery"; belief_ids: string[]; question_ids: string[]; question_types: string[];
  questions: number; battery_size: number; versions: number[]; passes: number; scorer_model_id: string;
  lanes: { lane: string; label: string; role: string; model_version: string | null }[];
  lane_options: { lane: string; label: string; role: string; default_selected: boolean }[];
  estimate: { calls: number; probe_usd: number; judge_usd: number; total_usd: number; suggested_cap_usd: number };
}

export interface TestRequest {
  versions?: number[]; scope?: "belief" | "battery"; lanes?: { lane: string }[]; passes?: number;
  spend_cap_usd?: number;
}

export async function getPublishState(customerId: number, draftId: number): Promise<PublishState> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/publish`);
}

export async function planDraftTest(customerId: number, draftId: number, body: TestRequest): Promise<TestPlan> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/wind-tunnel/plan`, { method: "POST", body: JSON.stringify(body) });
}

export async function testDraft(customerId: number, draftId: number, body: TestRequest):
  Promise<{ run_id: number; tests: number[]; state: PublishState }> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/wind-tunnel`, { method: "POST", body: JSON.stringify(body) });
}

export async function requestApproval(customerId: number, draftId: number, versionNumber: number, note = ""):
  Promise<PublishState> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/approvals`, {
    method: "POST", body: JSON.stringify({ version_number: versionNumber, note }),
  });
}

export async function approve(customerId: number, approvalId: number, body: { confirm_self?: boolean; reason?: string }):
  Promise<PublishState> {
  return apiFetch(`${base(customerId)}/approvals/${approvalId}/approve`, { method: "POST", body: JSON.stringify(body) });
}

export async function withdrawApproval(customerId: number, approvalId: number, reason = ""): Promise<PublishState> {
  return apiFetch(`${base(customerId)}/approvals/${approvalId}/withdraw`, {
    method: "POST", body: JSON.stringify({ reason }),
  });
}

export async function sendToPublisher(customerId: number, draftId: number, versionNumber: number,
                                      destinations: string[]): Promise<{ publishing_draft_id: number }> {
  return apiFetch(`${base(customerId)}/drafts/${draftId}/send-to-publisher`, {
    method: "POST", body: JSON.stringify({ version_number: versionNumber, destinations }),
  });
}

export async function listPublications(customerId: number):
  Promise<{ available: boolean; migration?: string; publications: Publication[] }> {
  return apiFetch(`${base(customerId)}/publications`);
}

export interface HandEntry {
  title?: string; destination: string; published_at: string; approved_by: string; url?: string;
  content?: string; content_hash?: string; draft_id?: number; draft_version?: number; action_id?: number;
  action_item_id?: number; belief_ids?: string[]; question_types?: string[]; wind_tunnel_run_id?: number; note?: string;
}

/** A piece published outside the platform, with the same fields as a
 *  platform publish (replaces the free-text deployment form). */
export async function recordPublication(customerId: number, body: HandEntry): Promise<Publication> {
  return apiFetch(`${base(customerId)}/publications`, { method: "POST", body: JSON.stringify(body) });
}

export interface RecheckSettings {
  enabled: boolean; delay_days: number; passes: number; run_cap_usd: number; monthly_cap_usd: number;
  max_per_tick: number; max_attempts: number; draft_test_cap_usd: number;
}

export async function getRecheckSettings(): Promise<{ settings: RecheckSettings; defaults: RecheckSettings }> {
  return apiFetch(`/content/settings/publication-recheck`);
}

export async function saveRecheckSettings(values: Partial<RecheckSettings>): Promise<{ settings: RecheckSettings }> {
  return apiFetch(`/content/settings/publication-recheck`, { method: "PUT", body: JSON.stringify({ values }) });
}

// ── What publications moved, and the precedent index (#134) ────────────────

export type OutcomeStatus = "not_measured" | "not_yet" | "moved" | "reversed";

export interface OutcomeCell { share: number | null; directional?: boolean; present?: number; answers?: number }

export interface OutcomeMetric {
  key: string;
  kind: "presence" | "when_present" | "qtype_primary";
  label: string;
  belief_id?: string;
  name?: string;
  question_types?: string[];
  before: OutcomeCell | null;
  after: OutcomeCell | null;
  delta: number | null;
  band: number | null;
  direction: "up" | "down" | null;
  counted: boolean;
}

export interface OutcomeFigures {
  ref?: { run_id: number; read_label: string };
  read?: { run_id: number; read_label: string };
  metrics?: OutcomeMetric[];
}

export interface PublicationOutcome {
  publication_id: number;
  title: string;
  destination: string;
  url: string | null;
  published_at: string;
  action_id: number | null;
  targets: { belief_ids: string[]; question_types: string[] };
  status: OutcomeStatus;
  status_label: string;
  resolved: boolean;
  resolved_at_read: number | null;
  resolved_at_label?: string;
  flag: "up" | "down" | null;
  note: string;
  restarted: boolean;
  /** After a resolution: did the move last at the next reads? */
  lasting: "held" | "faded" | "pending" | null;
  lasting_label: string | null;
  figures: OutcomeFigures;
  reads: { run_id: number; read_number: number; read_label: string; status: OutcomeStatus; flag: "up" | "down" | null;
           restarted: boolean; follow_up: boolean; note: string; figures: OutcomeFigures }[];
  // Admin only:
  tags?: { intervention_class: string | null; asset_type: string | null };
}

export interface PublicationMark {
  publication_id: number;
  published_at: string;
  title: string;
  status: OutcomeStatus;
  status_label: string;
  resolved_at_label?: string | null;
  lasting_label?: string | null;
  note: string;
}

export interface PublicationOutcomesView {
  available: boolean;
  note?: string;
  publications: PublicationOutcome[];
  marks: PublicationMark[];
  rules?: Record<string, string>;
  // Admin only:
  consent?: { consented: boolean; note: string; updated_by: string | null; updated_at: string | null };
  vertical?: string;
}

export const INTERVENTION_CLASSES: { id: string; label: string }[] = [
  { id: "accessibility", label: "Accessibility" },
  { id: "specificity_entity", label: "Specificity and entity" },
  { id: "retrieval_structure", label: "Retrieval structure" },
];

export async function getPublicationOutcomes(customerId: number): Promise<PublicationOutcomesView> {
  return apiFetch(`${base(customerId)}/publication-outcomes`);
}

export async function evaluatePublicationOutcomes(customerId: number): Promise<{ written: number; resolved: number }> {
  return apiFetch(`${base(customerId)}/publication-outcomes/evaluate`, { method: "POST" });
}

export async function tagPublication(customerId: number, publicationId: number,
                                     body: { intervention_class: string | null; asset_type: string | null }) {
  return apiFetch(`${base(customerId)}/publications/${publicationId}/tags`, { method: "PUT", body: JSON.stringify(body) });
}

export async function setPrecedentConsent(customerId: number, consented: boolean, note: string) {
  return apiFetch(`${base(customerId)}/precedent-consent`, { method: "PUT", body: JSON.stringify({ consented, note }) });
}

export interface PrecedentQuery {
  vertical?: string; belief_category?: string; intervention_class?: string; asset_type?: string;
  outcome?: "moved" | "reversed";
}

export async function queryPrecedents(q: PrecedentQuery):
  Promise<{ available: boolean; summary: { entries: number; rebuilt_at: string | null };
            precedents: import("./action-list").Precedent[] }> {
  const params = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]);
  return apiFetch(`/content/precedents?${params.toString()}`);
}

export async function rebuildPrecedents(): Promise<{ entries: number }> {
  return apiFetch(`/content/precedents/rebuild`, { method: "POST" });
}
